<template>
  <div class="be-room-card__rate" :class="{ 'is-expanded': isDetailShown, 'is-unavailable': !isAvailable }">
    <p class="be-room-card__rate-name">{{ rate.name }}</p>

    <!-- BENEFITS -->
    <div class="be-room-card__col be-room-card__col--benefits">
      <p class="be-room-card__includes-label">Price Includes</p>
      <ul class="be-room-card__includes">
        <li>
          <CreditCard class="be-room-card__include-icon" :class="{ 'is-success': rate.minimum_deposit_value > 0 }" :size="14" />
          <span>{{ prepaymentText }}</span>
          <span :id="`v3-prepayment-${rate.id}`" class="be-room-card__include-info"><Info :size="12" /></span>
          <b-tooltip :target="`v3-prepayment-${rate.id}`" triggers="hover" placement="top">
            <div class="p-2 text-left">{{ prepaymentTooltipMessage }}</div>
          </b-tooltip>
        </li>
        <li v-if="rate.rate.is_included_breakfast || !isNoMealTextHidden">
          <svg class="be-room-card__include-icon" :class="{ 'is-danger': !rate.rate.is_included_breakfast }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
            <path d="M7 2v20" />
            <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
          </svg>
          <span>{{ rate.rate.is_included_breakfast ? 'Breakfast Included' : 'No Meal Included' }}</span>
        </li>
        <li v-if="facilityNames">
          <CheckCircle class="be-room-card__include-icon" :size="14" />
          <span class="be-room-card__amenities">{{ facilityNames }}</span>
        </li>
        <li v-if="$isNotEmptyV2(rate.cancellation_policy)">
          <Calendar class="be-room-card__include-icon" :size="14" />
          <span>{{ rate.cancellation_policy.title }}</span>
        </li>
      </ul>
      <p class="be-room-card__more-detail" @click="$emit('open-rate-detail')">
        {{ isDetailShown ? $t('hotel.hide-information') : $t('merchant.more-detail') }}
      </p>
    </div>

    <!-- GUEST -->
    <div class="be-room-card__col be-room-card__col--guest">
      <div :id="`v3-guest-${rate.id}`" class="be-room-card__guest-info">
        <div class="be-room-card__guest-icons">
          <Users :size="16" />
          <Info :size="12" />
        </div>
        <span class="be-room-card__guest-included">
          {{ rate.rate.guest_included }} {{ $pluralWord(rate.rate.guest_included, 'guest') }} included
        </span>
      </div>
      <b-tooltip :target="`v3-guest-${rate.id}`" triggers="hover" placement="bottom">
        <div class="p-2 text-left">{{ guestCapacityMessage }}</div>
      </b-tooltip>
    </div>

    <!-- TOTAL ROOM -->
    <RoomCardRateQty
      :rate="rate"
      :type="type"
      :avail="avail"
      :max-qty="maxQty"
      :selected-qty="selectedQty"
      :show-rooms-left="showRoomsLeft"
      :room-left-text="roomLeftText"
      @select="$emit('select-qty', $event)"
      @open-modal="$emit('open-qty-modal')" />

    <!-- PRICE -->
    <RoomCardRatePrice
      :rate="rate"
      :type="type"
      :avail="avail" />

    <!-- ACTIONS -->
    <div v-if="isAvailable" class="be-room-card__actions">
      <button type="button" class="be-room-card__book be-press" @click="$emit('book-now')">{{ $t('hotel.book-now') }}</button>
      <button type="button" class="be-room-card__addcart be-press" @click="$emit('add-to-cart')">Add to Cart</button>
    </div>

    <!-- INLINE DETAIL -->
    <CollapseTransition>
      <RoomCardRateDetail v-if="isDetailShown" :rate="rate" :avail="avail" />
    </CollapseTransition>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { CreditCard, CheckCircle, Calendar, Users, Info } from 'lucide-vue';
  import RoomCardRateQty from './RoomCardRateQty.vue';
  import RoomCardRatePrice from './RoomCardRatePrice.vue';
  import RoomCardRateDetail from './RoomCardRateDetail.vue';
  import CollapseTransition from '@/components/general/CollapseTransition.vue';
  import { prefersReducedMotion } from '@/utils/index.js';

  /* clears the fixed landing navbar (64px) so the expanded row is not tucked under it */
  const SCROLL_TOP_OFFSET = 80;

  export default {
    name: 'RoomCardRateRow',
    components: {
      CreditCard,
      CheckCircle,
      Calendar,
      Users,
      Info,
      RoomCardRateQty,
      RoomCardRatePrice,
      RoomCardRateDetail,
      CollapseTransition
    },
    props: {
      rate: {
        type: Object,
        default: () => ({})
      },
      type: {
        type: Object,
        default: () => ({})
      },
      avail: {
        type: Number,
        default: 0
      },
      maxQty: {
        type: Number,
        default: 1
      },
      selectedQty: {
        type: Number,
        default: 1
      },
      isDetailShown: {
        type: Boolean,
        default: false
      },
      showRoomsLeft: {
        type: Boolean,
        default: false
      },
      roomLeftText: {
        type: String,
        default: ''
      }
    },
    computed: {
      isAvailable(){
        return this.rate.bookable && this.avail > 0;
      },
      isNoMealTextHidden(){
        return this.getMerchant().search_room_hide_no_meal_included === true;
      },
      facilityNames(){
        const facilities = this.type.facility || [];
        return facilities.map(facility => facility.name).join(', ');
      },
      prepaymentText(){
        return this.rate.minimum_deposit_value > 0 ? 'Prepayment is required' : 'No Prepayment required';
      },
      prepaymentTooltipMessage(){
        return this.rate.minimum_deposit_value > 0
          ? 'A payment is necessary to complete the reservation'
          : 'Payment will be collected when you arrive at the hotel.';
      },
      guestCapacityMessage(){
        const guestIncluded = this.rate.rate.guest_included;
        const maxGuests = this.rate.rate.max_guests || this.rate.room_type.max_guests;
        const base = `This ${this.type.type} includes ${guestIncluded} ${this.$pluralWord(guestIncluded, 'guest')}`;
        if(!maxGuests || maxGuests <= guestIncluded) return `${base}.`;
        return `${base}, with a maximum capacity of ${maxGuests} ${this.$pluralWord(maxGuests, 'guest')}. Additional guests will incur extra charges.`;
      }
    },
    watch: {
      isDetailShown(isShown){
        if(isShown) this.$nextTick(this.scrollRowIntoFocus);
      }
    },
    methods: {
      ...mapGetters(['getMerchant']),
      scrollRowIntoFocus(){
        const top = this.$el.getBoundingClientRect().top + window.pageYOffset - SCROLL_TOP_OFFSET;
        if('scrollBehavior' in document.documentElement.style && !prefersReducedMotion()){
          window.scrollTo({ top: top, behavior: 'smooth' });
          return;
        }
        window.scrollTo(0, top);
      }
    }
  }
</script>

<style scoped>
/* Mobile stacks the row into: name / benefits / price / guest+qty pair / actions.
   Every cell is placed explicitly so the pair is the only one sharing a row and
   the order stays fixed whether or not the optional cells render. */
.be-room-card__rate {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0 0 20px;
  border-bottom: 1px solid var(--be-v3-border, #d5d9e0);
}
.be-room-card__rate:last-child {
  border-bottom: none;
}
.be-room-card__rate.is-expanded {
  position: relative;
  z-index: 1051;
  background: #fff;
}
.be-room-card__rate-name {
  grid-column: 1 / -1;
  grid-row: 1;
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
  font-weight: 800;
  /* stroke so the weight bump is visible even with a single-file merchant font */
  -webkit-text-stroke: 0.3px currentColor;
  font-size: var(--be-v3-text-subhead, 0.9375rem);
  line-height: 1.3;
  color: #1a1a2e;
  margin: 0;
  padding: 22px 0 12px;
  border-bottom: 1px solid var(--be-v3-border-soft, #e3e6eb);
}
.be-room-card__col--benefits {
  grid-column: 1 / -1;
  grid-row: 2;
  padding: 16px 0 0;
  min-width: 0;
}
.be-room-card__col--price {
  grid-column: 1 / -1;
  grid-row: 3;
  padding: 4px 0 16px;
}
/* guest and qty share row 4 and read as one boxed strip between price and actions */
.be-room-card__col--guest,
.be-room-card__col--room {
  grid-row: 4;
  padding: 14px 10px;
  border-top: 1px solid var(--be-v3-border-soft, #e3e6eb);
  border-bottom: 1px solid var(--be-v3-border-soft, #e3e6eb);
  min-width: 0;
}
.be-room-card__col--guest {
  grid-column: 1;
  text-align: center;
}
.be-room-card__col--room {
  grid-column: 2;
  text-align: right;
  border-left: 1px solid var(--be-v3-border-soft, #e3e6eb);
}
.be-room-card__actions {
  grid-column: 1 / -1;
  grid-row: 5;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding-top: 16px;
}
.be-room-card__detail {
  grid-row: 6;
}
.be-room-card__book,
.be-room-card__addcart {
  width: 100%;
  border-radius: var(--be-v3-button-radius, 999px);
  padding: 11px 14px;
  font-weight: 600;
  font-size: var(--be-v3-text-secondary, 0.8125rem);
  cursor: pointer;
}
/* Book Now stays first in the DOM as the primary action; the design places
   Add to Cart on the left, so the pair is reordered visually only */
.be-room-card__book {
  order: 2;
  background: var(--merchant-button-color, var(--merchant-primary-color, #79a33a));
  color: #fff;
  border: none;
}
.be-room-card__addcart {
  order: 1;
  background: #fff;
  color: var(--merchant-primary-color, #79a33a);
  border: 1px solid var(--merchant-primary-color, #79a33a);
}
.be-room-card__includes-label {
  font-size: var(--be-v3-text-caption, 0.75rem);
  font-weight: 400;
  color: #6d6d6d;
  margin-bottom: 6px;
}
.be-room-card__includes {
  list-style: none;
  padding: 0;
  margin: 0 0 8px;
}
.be-room-card__includes li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: var(--be-v3-text-secondary, 0.8125rem);
  line-height: 1.4;
  color: #333;
  margin-bottom: 6px;
  overflow-wrap: anywhere;
}
.be-room-card__include-icon {
  flex-shrink: 0;
  color: #555;
  margin-top: 1px;
}
.be-room-card__include-icon.is-success {
  color: #28a745;
}
.be-room-card__include-icon.is-danger {
  color: #dc3545;
}
.be-room-card__include-info {
  display: inline-flex;
  color: #555;
  cursor: help;
  margin-top: 1px;
}
.be-room-card__amenities {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.be-room-card__more-detail {
  color: var(--merchant-primary-color, #79a33a);
  font-weight: 400;
  font-size: var(--be-v3-text-secondary, 0.8125rem);
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;
}
.be-room-card__guest-info {
  display: inline-block;
  cursor: help;
}
.be-room-card__guest-icons {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 5px;
  color: #555;
  margin-bottom: 4px;
}
.be-room-card__guest-included {
  color: #1a1a2e;
  font-weight: 400;
  font-size: var(--be-v3-text-secondary, 0.8125rem);
  line-height: 1.35;
  text-decoration: underline;
  display: inline-block;
}
@media screen and (min-width: 992px) {
  .be-room-card__rate {
    grid-template-columns: var(
      --be-v3-rate-grid,
      minmax(0, 2.2fr) minmax(72px, 0.6fr) minmax(78px, 0.6fr) minmax(158px, 1.4fr)
    );
    padding: 0;
  }
  /* only lifted against the dimmed backdrop, which is desktop-only */
  .be-room-card__rate.is-expanded {
    border-radius: var(--be-v3-radius, 12px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  }
  .be-room-card__rate-name {
    padding: 20px 16px 14px;
    min-width: 0;
  }
  .be-room-card__col {
    padding: 20px 12px;
    /* grid items default to min-width:auto — without this the benefits text refuses
       to shrink below its content width and spills across the column dividers */
    min-width: 0;
  }
  .be-room-card__col + .be-room-card__col {
    border-left: 1px solid var(--be-v3-border-soft, #e3e6eb);
  }
  /* the three left cells span rows 2-3 so their dividers run the full height while
     the actions sit under the price in column 4 */
  .be-room-card__col--benefits {
    grid-column: 1;
    grid-row: 2 / 4;
  }
  .be-room-card__col--guest {
    grid-column: 2;
    grid-row: 2 / 4;
    text-align: center;
    border-top: none;
    border-bottom: none;
  }
  .be-room-card__col--room {
    grid-column: 3;
    grid-row: 2 / 4;
    text-align: center;
    border-top: none;
    border-bottom: none;
  }
  .be-room-card__col--price {
    grid-column: 4;
    grid-row: 2;
    padding-bottom: 0;
  }
  /* with no actions cell the price is the only thing in column 4, so it has to
     span both rows itself or its divider stops at the badge and looks clipped */
  .is-unavailable .be-room-card__col--price {
    grid-row: 2 / 4;
    padding-bottom: 20px;
  }
  .be-room-card__col--guest .be-room-card__guest-icons {
    justify-content: center;
  }
  .be-room-card__actions {
    grid-column: 4;
    grid-row: 3;
    display: block;
    padding: 8px 12px 20px;
    border-left: 1px solid var(--be-v3-border-soft, #e3e6eb);
  }
  .be-room-card__book,
  .be-room-card__addcart {
    order: 0;
    display: block;
    max-width: 200px;
    margin-left: auto;
    padding: 9px 14px;
  }
  .be-room-card__addcart {
    margin-top: 6px;
  }
  .be-room-card__detail {
    grid-row: 4;
  }
}
/* Jost (the v3 default content font) is a real variable font that already gets
   heavier at font-weight 800 — the stroke above was tuned for BeV3Sans/merchant
   single-file fonts and doubles up on Jost, so drop it when no custom font is set. */
.be-v3-content-font-default .be-room-card__rate-name {
  -webkit-text-stroke: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/room-card/RoomCardRateRow.vue